import React, { useEffect, useRef, useState } from 'react';
import { MdArrowLeft, MdArrowRight } from 'react-icons/md';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { dispatchTrackingEvent } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import runAfterNextPaint from '@PROJECT_ROOT/assets/responsive-frontend/js/services/runAfterNextPaint';

const MAX_VISIBLE_INDICATORS = 4;

function Carousel({
    data,
    isGalleryLoaded = true,
    autoplay,
    emitClick,
    usedOnMap,
    imageWidth,
    imageHeight,
    imageSourceElements,
    firstImageLazy,
    galleryFetching,
    currentID,
    hideIndicators,
    customObjectFit,
    trackingEvents,
    onCurrentIndexChanged,
    fetchGallery,
    indexToJumpTo = null,
    maxHeight = null,
    anchorTagUrl,
    anchorTagLabel,
    isMobileScreen,
    onImageClick,
    searchResultsSingleUnitCardOverlayContent,
    imagePriorityLCP,
}) {
    const isMounted = useRef(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [anchorTagTarget, setAnchorTagTarget] = useState(null);
    const [anchorTagRel, setAnchorTagRel] = useState(null);
    const [indicatorAnimationDirection, setIndicatorAnimationDirection] = useState(null);
    const [isHydrated, setIsHydrated] = useState(false);
    const indexChangedAfterJump = useRef(false);
    const bootstrapInstances = useRef({});
    const carouselElement = useRef(null);
    const startX = useRef(null);
    const startY = useRef(null);
    const handleNextRef = useRef(null);
    const handlePrevRef = useRef(null);
    const slideDirection = useRef('next');
    const carouselElementId = usedOnMap ? `carousel${data.id}mapCard` : `carousel${data.id || currentID}`;
    const autoplayIntervalMs = 3000;
    // DESC: Ako trebamo pratiti index, to možemo jedino tako da sami kontroliramo njegovu promjenu.
    const useCustomControls = !!onCurrentIndexChanged;

    const getVisibleIndicatorIndexes = () => {
        const totalItems = data.items.length;
        // DESC: Po dizajnu smo željeli 4 indikatora i u slučaju da kompletna galerija još nije učitana.
        if (totalItems <= MAX_VISIBLE_INDICATORS) {
            return Array.from(
                { length: MAX_VISIBLE_INDICATORS },
                (_, index) => (index < totalItems ? index : null),
            );
        }
        let firstVisibleIndex;
        if (currentIndex === 0) {
            firstVisibleIndex = 0;
        } else if (currentIndex === totalItems - 1) {
            firstVisibleIndex = totalItems - MAX_VISIBLE_INDICATORS;
        } else if (slideDirection.current === 'prev') {
            // DESC: Aktivnost indikatora na drugoj poziciji.
            firstVisibleIndex = currentIndex - 1;
        } else {
            // DESC: Aktivnost indikatora na trecoj(predzadnjoj) poziciji.
            firstVisibleIndex = currentIndex - 2;
        }
        firstVisibleIndex = Math.max(0, Math.min(firstVisibleIndex, totalItems - MAX_VISIBLE_INDICATORS));

        return Array.from({ length: MAX_VISIBLE_INDICATORS }, (_, offset) => firstVisibleIndex + offset);
    };

    const visibleIndicatorIndexes = getVisibleIndicatorIndexes();

    useEffect(() => {
        setAnchorTagTarget(anchorTagUrl && !isMobileScreen ? '_blank' : null);
        setAnchorTagRel(anchorTagUrl && !isMobileScreen ? 'noreferrer' : null);
    }, [anchorTagUrl, isMobileScreen]);

    const handleTrackingEvents = () => {
        if (!IS_FRAME_SERVICE) {
            if (trackingEvents.beachGallery
                && (trackingEvents.beachGallery?.activeLocationBeaches === trackingEvents.beachGallery?.mainTextForMatch)) {
                dispatchTrackingEvent(trackingEvents.beachGallery.mainOnNext);
            }
            if (trackingEvents.beachGallery
                && (trackingEvents.beachGallery?.activeLocationBeaches === trackingEvents.beachGallery?.otherTextForMatch)) {
                dispatchTrackingEvent(trackingEvents.beachGallery.otherOnNext);
            }
            if (trackingEvents.facilityUnitCard && trackingEvents.facilityUnitCard.onNext) {
                dispatchTrackingEvent(trackingEvents.facilityUnitCard.onNext);
            }
            if (trackingEvents.searchResultsGalleryChangeImage?.eventName) {
                dispatchTrackingEvent(trackingEvents.searchResultsGalleryChangeImage.eventName);
            }
        }
    };

    const handlePrev = (event) => {
        slideDirection.current = 'prev';
        event?.preventDefault?.();
        if (emitClick && event) {
            emitClick(event);
        }
        runAfterNextPaint(() => {
            if (isGalleryLoaded) {
                bootstrapInstances.current[carouselElementId]?.prev();
            } else {
                fetchGallery?.('prev');
            }
        });
        if (trackingEvents?.searchResultsGalleryChangeImage) {
            handleTrackingEvents();
        }
    };

    const handleNext = (event) => {
        slideDirection.current = 'next';
        event?.preventDefault?.();
        if (emitClick && event) {
            emitClick(event);
        }
        runAfterNextPaint(() => {
            if (isGalleryLoaded) {
                bootstrapInstances.current[carouselElementId]?.next();
            } else {
                fetchGallery?.('next');
            }
        });
        if (trackingEvents) {
            handleTrackingEvents();
        }
    };

    handleNextRef.current = handleNext;
    handlePrevRef.current = handlePrev;

    useEffect(() => {
        if (!isMounted.current) return;
        if (indexToJumpTo === null) return;
        const instance = bootstrapInstances.current[carouselElementId];
        if (!instance) return;
        indexChangedAfterJump.current = true;
        instance.to(indexToJumpTo);
    }, [indexToJumpTo, carouselElementId]);

    useEffect(() => {
        if (!isMounted.current) return;
        if (indexChangedAfterJump.current) {
            indexChangedAfterJump.current = false;
            return;
        }
        if (useCustomControls) {
            onCurrentIndexChanged(currentIndex);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex]);

    useEffect(() => {
        let isDisposed = false;
        let retryTimeout = null;
        let $carousel = null;
        const carouselInstances = bootstrapInstances.current;
        const getBootstrapJQuery = () => {
            if (window.jQuery?.fn?.carousel) {
                return window.jQuery;
            }
            if (window.$?.fn?.carousel) {
                return window.$;
            }

            return null;
        };
        const handleBootstrapSlideStarted = (event) => {
            if (typeof event.from !== 'number' || typeof event.to !== 'number') return;
            const direction = event.direction === 'right' ? 'prev' : 'next';
            const isSingleSlideMovement = Math.abs(event.to - event.from) === 1;
            slideDirection.current = direction;
            setIndicatorAnimationDirection(isSingleSlideMovement ? direction : null);
        };
        const handleBootstrapSlideChanged = (event) => {
            if (typeof event.to !== 'number') return;
            slideDirection.current = event.direction === 'right' ? 'prev' : 'next';
            setCurrentIndex(event.to);
            setIndicatorAnimationDirection(null);
        };
        const initializeCarousel = (attempt = 0) => {
            if (isDisposed) return;
            const currentCarouselElement = carouselElement.current;
            const jQueryInstance = getBootstrapJQuery();
            if (!currentCarouselElement || !currentCarouselElement.isConnected || !jQueryInstance) {
                // DESC: Zbog SSR-a odnosno zbog hidracije jQuery se moze ucitati naknadno, ovim osiguravamo da se inicijalizacija slidera odradi naknando.
                // DESC: Pokusat ce 50 puta svakih 50 milisekundi, dojavit ce error ako ne uspije inicijalizacija.
                if (attempt < 50) {
                    retryTimeout = window.setTimeout(() => {
                        initializeCarousel(attempt + 1);
                    }, 50);
                } else {
                    reportError(new Error(`Neuspješno inicijaliziranje bootstrap carousel-a za id: ${carouselElementId}`));
                }

                return;
            }
            $carousel = jQueryInstance(currentCarouselElement);
            $carousel.carousel({
                interval: autoplay ? autoplayIntervalMs : false,
                ride: autoplay ? 'carousel' : false,
                touch: false,
                keyboard: true,
                wrap: true,
            });
            carouselInstances[carouselElementId] = {
                to: (index) => {
                    $carousel.carousel(index);
                },
                next: () => {
                    $carousel.carousel('next');
                },
                prev: () => {
                    $carousel.carousel('prev');
                },
            };
            $carousel.on(
                'slide.bs.carousel',
                handleBootstrapSlideStarted,
            );
            $carousel.on(
                'slid.bs.carousel',
                handleBootstrapSlideChanged,
            );
        };
        initializeCarousel();

        return () => {
            isDisposed = true;
            if (retryTimeout !== null) {
                window.clearTimeout(retryTimeout);
            }
            if ($carousel) {
                const carouselInstance = $carousel.data('bs.carousel');
                $carousel.off(
                    'slide.bs.carousel',
                    handleBootstrapSlideStarted,
                );
                $carousel.off(
                    'slid.bs.carousel',
                    handleBootstrapSlideChanged,
                );
                if (carouselInstance?.touchTimeout) {
                    window.clearTimeout(carouselInstance.touchTimeout);
                    carouselInstance.touchTimeout = null;
                }
                $carousel.carousel('pause');
                /* eslint-disable no-underscore-dangle */
                if (carouselInstance?._interval) {
                    window.clearInterval(carouselInstance._interval);
                    carouselInstance._interval = null;
                }
                /* eslint-enable no-underscore-dangle */
                $carousel.carousel('dispose');
            }
            delete carouselInstances[carouselElementId];
        };
    }, [autoplay, carouselElementId]);

    useEffect(() => {
        const currentCarouselElement = carouselElement.current;
        if (!currentCarouselElement) {
            return undefined;
        }
        const handleTouchStart = (event) => {
            if (!event.touches?.length) return;
            startX.current = event.touches[0].clientX;
            startY.current = event.touches[0].clientY;
        };
        const handleTouchEnd = (event) => {
            if (!event.changedTouches?.length || startX.current === null || startY.current === null) return;
            const endX = event.changedTouches[0].clientX;
            const endY = event.changedTouches[0].clientY;
            const diffX = startX.current - endX;
            const diffY = startY.current - endY;
            const absX = Math.abs(diffX);
            const absY = Math.abs(diffY);
            // DESC: Triggera se samo ako je horizontalni swipe veći od vertikalnog.
            if (absX > 50 && absX > absY) {
                if (diffX > 0) {
                    handleNextRef.current?.();
                } else {
                    handlePrevRef.current?.();
                }
            }
            startX.current = null;
            startY.current = null;
        };
        currentCarouselElement.addEventListener('touchstart', handleTouchStart, { passive: true });
        currentCarouselElement.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            currentCarouselElement.removeEventListener('touchstart', handleTouchStart);
            currentCarouselElement.removeEventListener('touchend', handleTouchEnd);
        };
    }, [carouselElementId]);

    // DESC: Mora biti na kraju, ne želimo da se neki useEffecti izvrše inicijalno
    useEffect(() => {
        isMounted.current = true;
        setIsHydrated(true);
    }, []);

    const mainImageElement = (item, index) => {
        const isPriorityImage = index === 0 && (imagePriorityLCP ?? !firstImageLazy);

        return (
            <img
                loading={isPriorityImage ? 'eager' : 'lazy'}
                fetchpriority={isPriorityImage ? 'high' : 'low'}
                src={item.image.sourceURL}
                srcSet={imageSourceElements ? null : [
                    `${item.image.sourceURL} 1x`,
                    item.imageSourceURLMediumSizeAlternative ? `${item.imageSourceURLMediumSizeAlternative} 2x` : '',
                    item.imageSourceURLLargeSizeAlternative ? `${item.imageSourceURLLargeSizeAlternative} 3x` : '',
                ]
                    .filter(Boolean)
                    .join(', ')}
                className="carousel-item__image d-block w-100 carousel-img facility-unit-card-img-br unit-details-slider-img-br facility-unit-img-br"
                alt={item.image.altText}
                title={item.image.titleText}
                width={imageWidth}
                height={imageHeight}
                style={{
                    aspectRatio: `${imageWidth} / ${imageHeight}`,
                    objectFit: customObjectFit || 'contain',
                }}
            />
        );
    };

    const handleCarouselKeyDown = (event) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            handlePrev();
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            handleNext();
        }
    };

    const CarouselItemsWrapper = anchorTagUrl ? 'a' : 'div';

    return (
        data && (
            <div
                ref={carouselElement}
                id={carouselElementId}
                className="carousel slide"
                role="region"
                aria-roledescription="carousel"
                aria-label="Image carousel"
                style={{ ...(maxHeight != null && { maxHeight: `${maxHeight}px` }) }}
            >
                {isHydrated && !hideIndicators && (
                    <ol
                        className={[
                            'carousel__custom-indicators',
                            indicatorAnimationDirection
                                ? `carousel__custom-indicators--eat-${indicatorAnimationDirection}`
                                : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        aria-label="Carousel slide navigation"
                    >
                        {visibleIndicatorIndexes.map((slideIndex, indicatorIndex) => {
                            const isPlaceholder = slideIndex === null;
                            const isActive = slideIndex === currentIndex;

                            return (
                                <li
                                    key={isPlaceholder ? `placeholder-${indicatorIndex}` : slideIndex}
                                    className="carousel__custom-indicators-item"
                                >
                                    <span
                                        className={[
                                            'carousel-indicators__item',
                                            isActive ? 'active' : '',
                                            isPlaceholder ? 'carousel-indicators__item--placeholder' : '',
                                        ]
                                            .filter(Boolean)
                                            .join(' ')}
                                    />
                                </li>
                            );
                        })}
                    </ol>
                )}
                <div className="carousel-inner">
                    {galleryFetching && (
                        <div className="swiper__loader" />
                    )}
                    <CarouselItemsWrapper
                        className={`carousel-items ${anchorTagUrl ? 'carousel-items__link' : ''}`}
                        href={anchorTagUrl ?? null}
                        onClick={onImageClick}
                        aria-label={anchorTagLabel}
                        target={anchorTagTarget}
                        rel={anchorTagRel}
                    >
                        {data.items.map((item, index) => (
                            <span className={`carousel-item ${index === 0 ? 'active' : ''}`} key={index}>
                                {item.image && (
                                    imageSourceElements ? (
                                        <picture className="carousel-item__image-wrapper">
                                            {imageSourceElements(item, index)}
                                            {mainImageElement(item, index)}
                                        </picture>
                                    ) : mainImageElement(item, index)
                                )}
                                {item.title && (
                                    <span className="carousel-item__title">
                                        {item.title}
                                    </span>
                                )}
                            </span>
                        ))}
                    </CarouselItemsWrapper>
                </div>
                <button
                    className="carousel-control-prev carousel__navigation"
                    type="button"
                    onClick={handlePrev}
                    onKeyDown={handleCarouselKeyDown}
                    aria-label="Previous slide"
                >
                    <span className="carousel-control-prev-icon" aria-hidden="true">
                        <MdArrowLeft className="carousel-control-prev-icon__arrow" />
                    </span>
                    <span className="sr-only">Previous</span>
                </button>
                <button
                    className="carousel-control-next carousel__navigation"
                    type="button"
                    onClick={handleNext}
                    onKeyDown={handleCarouselKeyDown}
                    aria-label="Next slide"
                >
                    <span className="carousel-control-next-icon" aria-hidden="true">
                        <MdArrowRight className="carousel-control-prev-icon__arrow" />
                    </span>
                    <span className="sr-only">Next</span>
                </button>
                {currentIndex === 0 && searchResultsSingleUnitCardOverlayContent}
            </div>
        )
    );
}

export default Carousel;
